import type { ContentfulClientApi, EntryCollection } from 'contentful';
import { Logger } from '@thesavingsgroup/logger/Logger';
import type { Reporter } from '@thesavingsgroup/reporting/types/Reporter';
import type { TypeEmbeddableShoppingExperienceConfigurationSkeleton } from './types/TypeEmbeddableShoppingExperienceConfiguration';

type GetRemoteConfigurationDependencies = {
  client: ContentfulClientApi<undefined>;
  logger?: Logger;
  reporter: Reporter;
};

export type GetRemoteConfiguration = EntryCollection<
  TypeEmbeddableShoppingExperienceConfigurationSkeleton,
  undefined,
  string
> | null;

type GetRemoteConfigurationResponse = Promise<GetRemoteConfiguration>;

export const getConfigurationFromContentful =
  ({
    client,
    logger = new Logger(),
    reporter,
  }: GetRemoteConfigurationDependencies) =>
  async (configurationCode: string): GetRemoteConfigurationResponse => {
    logger.info(
      '[REMOTE_CONFIGURATION] Lookup by configuration code',
      configurationCode,
    );

    return client
      .getEntries<TypeEmbeddableShoppingExperienceConfigurationSkeleton>({
        content_type: 'embeddableShoppingExperienceConfiguration',
        'fields.configurationCode': configurationCode,
        limit: 1,
      })
      .then((response) => {
        if (!response.items.length) {
          logger.info(
            '[REMOTE_CONFIGURATION] Configuration not found',
            configurationCode,
          );
          return null;
        }

        logger.info('[REMOTE_CONFIGURATION] Found configuration', response);
        return response;
      })
      .catch((error) => {
        const message = `[REMOTE_CONFIGURATION_ERROR] ${error.message}`;

        // logging as info to avoid sending useless errors to the console in production
        logger.info(message, error);
        reporter.error(new Error(message), { originalException: error });

        return null;
      });
  };
